import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT, tr } from '../utils/translate';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import { fetchContacts, invalidateContacts, removeContact } from '../store/contactsSlice';
import Pagination from '../components/Pagination';
import CallButton from '../components/CallButton';
import { PageHeader, SearchInput, FilterPopover, MoreMenu } from '../components/ui/PageToolbar';
import { getSocket } from '../utils/socket';
import { usePermissions } from '../utils/permissions';
import { Plus, Pencil, Trash2, Loader2, Users, Columns3, Phone, Check, Download, Upload, Copy, X, AlertTriangle, AlertCircle, ChevronRight } from 'lucide-react';
import { formatDate } from '../utils/date';
import { srv } from '../utils/serverText';
import { fmtPhone } from '../utils/format';
import ContactMergeDialog from '../components/ContactMergeDialog';

function isReminderOverdue(reminderAt) {
  return !!reminderAt && new Date(reminderAt) < new Date();
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const LS_KEY = 'crm_contacts_columns';

/* ─── Helpers ──────────────────────────────────────────────── */
function renderFieldValue(field, value) {
  if (value === undefined || value === null || value === '') return null;
  if (field.type === 'boolean')     return value ? tr('shared.yes') : tr('shared.no');
  if (field.type === 'multiselect') return Array.isArray(value) ? value.join(', ') : String(value);
  if (field.type === 'date')        { try { return formatDate(value); } catch { return value; } }
  if (field.type === 'file')        return value ? tr('shared.fileLabel') : null;
  return String(value);
}

/* ─── CSV parse (client-side preview for import) ──────────── */
function parseCsv(text) {
  const parseRow = (line) => {
    const cells = []; let inQ = false, cell = '';
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === ',' && !inQ) { cells.push(cell.trim()); cell = ''; }
      else { cell += ch; }
    }
    cells.push(cell.trim());
    return cells.map(c => c.replace(/^"|"$/g, '').trim());
  };
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseRow(lines[0]);
  const rows    = lines.slice(1).map(l => parseRow(l));
  return { headers, rows };
}

/* ─── Delete confirm modal ─────────────────────────────────── */
function DeleteConfirm({ name, count, onConfirm, onClose, loading }) {
  const t = useT();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h3 className="text-base font-semibold text-ink mb-2">{t('loc.contactsPage.deleteContact')}</h3>
        <p className="text-sm text-ink-secondary mb-6">
          {count
            ? <><span className="font-medium text-red-600">{t('loc.contactsPage.text', { count })}</span> {' '}{t('loc.contactsPage.contactsWillBeDeleted')}</>
            : <><span className="font-medium text-ink">{name}</span> {' '}{t('loc.contactsPage.willBeDeleted')}</>
          } {' '}{t('loc.contactsPage.thisActionCannotBeUndone')}
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-md btn-secondary flex-1">{t('loc.contactsPage.cancel')}</button>
          <button onClick={onConfirm} disabled={loading} className="btn-md btn-danger flex-1 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('loc.contactsPage.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Import Modal ─────────────────────────────────────────── */
function ImportModal({ onClose, onDone }) {
  const t = useT();
  const [file,      setFile]      = useState(null);
  const [preview,   setPreview]   = useState(null); // { headers, rows }
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const fileRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith('.csv')) { toast.error(t('loc.contactsPage.onlyCsvFilesAreAccepted')); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCsv(e.target.result);
      setPreview(parsed);
    };
    reader.readAsText(f, 'utf-8');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/contacts/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || t('loc.contactsPage.importError'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-2xl flex flex-col max-h-[85dvh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100 shrink-0">
          <h2 className="font-semibold text-ink flex items-center gap-2">
            <Upload className="w-4 h-4 text-primary-600" /> {' '}{t('loc.contactsPage.csvImport')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>

        {result ? (
          <div className="p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-emerald-700" />
            </div>
            <p className="text-lg font-bold text-ink mb-1">{t('loc.contactsPage.importComplete')}</p>
            <p className="text-sm text-ink-secondary">
              <span className="text-emerald-700 font-semibold">{t('loc.contactsPage.text2', { created: result.created })}</span> {' '}{t('loc.contactsPage.contactsAdded')}{' '}
              <span className="text-ink-tertiary">{t('loc.contactsPage.text3', { skipped: result.skipped })}</span> {' '}{t('loc.contactsPage.skippedExistingOrEmpty')}
            </p>
            <button onClick={onClose} className="btn-primary btn-md mt-6">{t('loc.contactsPage.close')}</button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              {/* Format hint */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                <p className="font-semibold mb-1">{t('loc.contactsPage.csvFileFormat')}</p>
                <code className="font-mono">Ism,Telefon,Email</code><br/>
                <code className="font-mono">Alibek Karimov,998901234567,ali@mail.com</code>
                <p className="mt-1 text-amber-700">{t('loc.contactsPage.theFirstRowMustContain')}</p>
              </div>

              {/* Drop zone */}
              {!preview ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-surface-300 rounded-2xl p-10 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors"
                >
                  <Upload className="w-8 h-8 text-ink-disabled mx-auto mb-2" />
                  <p className="text-sm font-medium text-ink-secondary">{t('loc.contactsPage.dropTheCsvFileHere')}</p>
                  <p className="text-xs text-ink-tertiary mt-1">{t('loc.contactsPage.orClickToChoose')}</p>
                  <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                    onChange={e => handleFile(e.target.files?.[0])} />
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-ink">{t('loc.contactsPage.rows', { v: file?.name, length: preview.rows.length })}</p>
                    <button onClick={() => { setFile(null); setPreview(null); }} className="text-xs text-ink-tertiary hover:text-red-600 flex items-center gap-1">
                      <X className="w-3 h-3" /> {' '}{t('loc.contactsPage.clear')}
                    </button>
                  </div>
                  <div className="overflow-x-auto border border-surface-200 rounded-xl">
                    <table className="w-full text-xs">
                      <thead className="bg-surface-50">
                        <tr>{preview.headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-semibold text-ink-tertiary whitespace-nowrap">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-surface-100">
                        {preview.rows.slice(0, 10).map((row, i) => (
                          <tr key={i} className="hover:bg-surface-50">
                            {preview.headers.map((_, j) => (
                              <td key={j} className="px-3 py-1.5 text-ink-secondary whitespace-nowrap max-w-[160px] truncate">{row[j] || '—'}</td>
                            ))}
                          </tr>
                        ))}
                        {preview.rows.length > 10 && (
                          <tr><td colSpan={preview.headers.length} className="px-3 py-2 text-center text-ink-tertiary">{t('loc.contactsPage.more')}{' '} {preview.rows.length - 10} {' '}{t('loc.contactsPage.rows2')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t border-surface-100 shrink-0">
              <button onClick={onClose} className="btn-secondary btn-md">{t('loc.contactsPage.cancel')}</button>
              <button onClick={handleImport} disabled={!preview || importing}
                className="btn-primary btn-md flex items-center gap-2">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {t('loc.contactsPage.import')}{preview?.rows.length || 0} {' '}{t('loc.contactsPage.text4')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Duplicates Modal ─────────────────────────────────────── */
function DuplicatesModal({ onClose, onMerged }) {
  const t = useT();
  const [groups,   setGroups]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [dialog,   setDialog]   = useState(null); // { group, keepId, mergeIds }
  const [keepIds,  setKeepIds]  = useState({});   // { phone: contactId }

  useEffect(() => {
    axios.get(`${API}/contacts/duplicates`)
      .then(r => {
        setGroups(r.data.groups || []);
        const defaults = {};
        (r.data.groups || []).forEach(g => { defaults[g._id] = g.contacts[0]._id; });
        setKeepIds(defaults);
      })
      .catch(() => toast.error(t('loc.contactsPage.failedToLoad')))
      .finally(() => setLoading(false));
  }, []);

  // Сначала показываем окно предпросмотра: что будет объединено и что перенести
  const handleMerge = (group) => {
    const keepId = keepIds[group._id];
    const mergeIds = group.contacts.filter(c => String(c._id) !== String(keepId)).map(c => c._id);
    setDialog({ group, keepId, mergeIds });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-xl flex flex-col max-h-[85dvh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100 shrink-0">
          <h2 className="font-semibold text-ink flex items-center gap-2">
            <Copy className="w-4 h-4 text-amber-700" /> {' '}{t('loc.contactsPage.duplicateContacts')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-primary-400" /></div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Check className="w-10 h-10 text-emerald-700 mb-3" />
              <p className="font-semibold text-ink">{t('loc.contactsPage.noDuplicates')}</p>
              <p className="text-xs text-ink-tertiary mt-1">{t('loc.contactsPage.allGood')}</p>
            </div>
          ) : (
            groups.map(group => (
              <div key={group._id} className="border border-amber-200 rounded-xl bg-amber-50/40 p-3">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {t('loc.contactsPage.duplicateNumbers', { count: group.count })}
                    </p>
                    <p className="text-xs text-ink-tertiary font-mono mt-0.5">{group._id}</p>
                  </div>
                  <button onClick={() => handleMerge(group)}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors">
                    <Copy className="w-3 h-3" />
                    {t('loc.contactsPage.merge')}
                  </button>
                </div>
                <div className="space-y-1.5">
                  {group.contacts.map(c => (
                    <label key={c._id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-white transition-colors">
                      <input type="radio" name={`keep-${group._id}`}
                        checked={String(keepIds[group._id]) === String(c._id)}
                        onChange={() => setKeepIds(prev => ({ ...prev, [group._id]: c._id }))}
                        className="accent-primary-600" />
                      <div>
                        <p className="text-sm font-medium text-ink">{srv(c.name)}</p>
                        <p className="text-xs text-ink-tertiary">{t('loc.contactsPage.created', { toLocaleDateString: formatDate(c.createdAt) })}</p>
                      </div>
                      {String(keepIds[group._id]) === String(c._id) && (
                        <span className="ml-auto text-xs text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">{t('loc.contactsPage.willBeSaved')}</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end px-5 py-4 border-t border-surface-100 shrink-0">
          <button onClick={onClose} className="btn-secondary btn-md">{t('loc.contactsPage.close')}</button>
        </div>
      </div>
      {dialog && (
        <ContactMergeDialog keepId={dialog.keepId} mergeIds={dialog.mergeIds}
          onClose={() => setDialog(null)}
          onDone={() => { setGroups(prev => prev.filter(g => g._id !== dialog.group._id)); setDialog(null); onMerged(); }} />
      )}
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
const CONTACTS_TTL = 5 * 60 * 1000;

export default function ContactsPage() {
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const t = useT();
  const perm = usePermissions();
  const canCreate = perm.can('contacts', 'create');
  const canEdit   = perm.can('contacts', 'edit');
  const canDelete = perm.can('contacts', 'delete');

  const { items: contacts, total, pages, loading, lastFetch, paramKey } = useSelector(s => s.contacts);

  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');

  const [selected,      setSelected]      = useState(new Set());
  const [deleting,      setDeleting]      = useState(null);   // contact obj OR 'bulk'
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showImport,    setShowImport]    = useState(false);
  const [showDupes,     setShowDupes]     = useState(false);

  const [orgSections, setOrgSections] = useState([]);
  const [colVis, setColVis] = useState(() => {
    // По умолчанию: телефон, ответственный, дата добавления (почта у большинства клиентов пустая); выбор владельца сохраняется
    try { return { phone: true, email: false, assignee: true, created: true, ...JSON.parse(localStorage.getItem(LS_KEY) || '{}') }; }
    catch { return { phone: true, email: false, assignee: true, created: true }; }
  });
  const [sort, setSort] = useState('-createdAt');
  const toggleSort = (field) => setSort(prev => (prev === field ? `-${field}` : field));

  const searchTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/organization/contact-fields`)
      .then(r => setOrgSections(Array.isArray(r.data.sections) ? r.data.sections : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const allFields = orgSections.flatMap(s => s.fields);
    if (!allFields.length) return;
    setColVis(prev => {
      const next = { ...prev };
      allFields.forEach(f => { if (!(f.id in next)) next[f.id] = false; });
      return next;
    });
  }, [orgSections]);

  useEffect(() => {
    if (Object.keys(colVis).length > 0) localStorage.setItem(LS_KEY, JSON.stringify(colVis));
  }, [colVis]);

  const toggleCol = (key) => setColVis(prev => ({ ...prev, [key]: !prev[key] }));

  const load = useCallback(async (p = 1, q = search) => {
    const params = { page: p, limit: 30 };
    if (q.trim()) params.search = q.trim();
    if (sort !== '-createdAt') params.sort = sort;
    const key = JSON.stringify(params);
    setPage(p);
    setSelected(new Set());
    if (key === paramKey && Date.now() - lastFetch < CONTACTS_TTL) return;
    try {
      await dispatch(fetchContacts(params)).unwrap();
    } catch { toast.error(t('loc.contactsPage.failedToLoadContacts')); }
  }, [dispatch, search, sort, paramKey, lastFetch]);

  useEffect(() => { load(1); }, [sort]); // eslint-disable-line

  // Real-time sync: boshqa foydalanuvchi kontakt qo'shsa/o'zgartirsa/o'chirsa —
  // ro'yxat avtomatik yangilanadi (refresh shart emas).
  useEffect(() => {
    const socket = getSocket();
    const refresh = () => { dispatch(invalidateContacts()); load(page); };
    socket.on('contact:created',      refresh);
    socket.on('contact:updated',      refresh);
    socket.on('contact:deleted',      refresh);
    socket.on('contact:bulk-deleted', refresh);
    return () => {
      socket.off('contact:created',      refresh);
      socket.off('contact:updated',      refresh);
      socket.off('contact:deleted',      refresh);
      socket.off('contact:bulk-deleted', refresh);
    };
  }, [dispatch, load, page]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(1, val), 400);
  };

  /* ─── Delete ─────────────────────────────────────────── */
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleting === 'bulk') {
        const ids = [...selected];
        await axios.delete(`${API}/contacts/bulk`, { data: { ids } });
        toast.success(t('contacts.deleted'));
        dispatch(invalidateContacts());
        load(page);
      } else {
        await axios.delete(`${API}/contacts/${deleting._id}`);
        toast.success(t('contacts.deleted'));
        dispatch(removeContact(deleting._id));
      }
      setDeleting(null);
    } catch (e) { toast.error(e.response?.data?.message || t('contacts.loadError')); }
    finally { setDeleteLoading(false); }
  };

  /* ─── Selection ──────────────────────────────────────── */
  const toggleOne = (id, e) => {
    e.stopPropagation();
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = (e) => {
    e.stopPropagation();
    if (selected.size === contacts.length) setSelected(new Set());
    else setSelected(new Set(contacts.map(c => c._id)));
  };

  /* ─── Normalize phones ───────────────────────────────── */
  const handleNormalizeAll = async () => {
    if (!window.confirm(t('loc.contactsPage.convertAllContactPhoneNumbers'))) return;
    try {
      const res = await axios.post(`${API}/contacts/normalize-all`);
      toast.success(t('loc.contactsPage.numbersNormalized', { updated: res.data.updated }));
      if (res.data.updated > 0) { dispatch(invalidateContacts()); load(1); }
    } catch { toast.error(t('loc.contactsPage.somethingWentWrong')); }
  };

  /* ─── Export CSV ─────────────────────────────────────── */
  const handleExport = () => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    axios.get(`${API}/contacts/export?${params.toString()}`, { responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url; a.download = 'contacts.csv'; a.click();
        URL.revokeObjectURL(url);
        toast.success(t('loc.contactsPage.csvDownloaded'));
      })
      .catch(() => toast.error(t('contacts.exportError')));
  };

  const allCustomFields = orgSections.flatMap(s => s.fields.map(f => ({ ...f, sectionName: s.name })));
  const allChecked  = contacts.length > 0 && selected.size === contacts.length;
  const someChecked = selected.size > 0 && selected.size < contacts.length;

  return (
    <div className="flex flex-col h-full">
      {/* Header: заголовок · поиск · колонки · «Ещё» · главное действие */}
      <PageHeader
        icon={Users}
        title={t('contacts.title')}
        count={!loading && total > 0 ? total : null}
        below={selected.size > 0 && (
          <div className="px-4 lg:px-6 py-2 border-t border-primary-100 bg-primary-50 flex items-center gap-3">
            <span className="text-sm font-semibold text-primary-700">{t('contacts.selectedN', { n: selected.size })}</span>
            {canDelete && (
              <button onClick={() => setDeleting('bulk')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> {t('contacts.deleteSelected').replace('{n}', selected.size)}
              </button>
            )}
            <button onClick={() => setSelected(new Set())} className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center text-ink-tertiary hover:text-ink hover:bg-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      >
        <SearchInput className="w-full sm:w-64 order-last sm:order-none" value={search} onChange={handleSearch} placeholder={t('contacts.searchPlaceholder')} />

        <FilterPopover label={t('contacts.columns')} icon={Columns3} width="w-64">
          <div>
            <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wider mb-2">{t('contacts.columnsTitle')}</p>
            <div className="flex items-center justify-between px-1 py-1.5 rounded-lg opacity-40">
              <span className="text-sm text-ink">{t('contacts.colContact')}</span>
              <div className="w-4 h-4 rounded border bg-primary-500 border-primary-500 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></div>
            </div>
            {[{ key: 'phone', label: t('contacts.colPhone') }, { key: 'assignee', label: t('contacts.colAssignee') }, { key: 'created', label: t('contacts.colCreated') }, { key: 'email', label: t('contacts.colEmail') }].map(col => (
              <button key={col.key} type="button" onClick={() => toggleCol(col.key)}
                className="flex items-center justify-between w-full px-1 py-1.5 rounded-lg hover:bg-surface-50 transition-colors">
                <span className="text-sm text-ink">{col.label}</span>
                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${colVis[col.key] ? 'bg-primary-500 border-primary-500' : 'border-surface-200'}`}>
                  {colVis[col.key] && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
              </button>
            ))}
          </div>
          {allCustomFields.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wider mb-2">{t('contacts.extraFields')}</p>
              {allCustomFields.map(field => (
                <button key={field.id} type="button" onClick={() => toggleCol(field.id)}
                  className="flex items-center justify-between w-full px-1 py-1.5 rounded-lg hover:bg-surface-50 transition-colors">
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-sm text-ink truncate">{field.key}</span>
                    <span className="text-xs text-ink-tertiary">{field.sectionName}</span>
                  </div>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ml-2 transition-colors ${colVis[field.id] ? 'bg-primary-500 border-primary-500' : 'border-surface-200'}`}>
                    {colVis[field.id] && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </FilterPopover>

        <MoreMenu items={[
          { icon: Download,  label: t('contacts.export'), onClick: handleExport },
          { icon: Upload,    label: t('contacts.import'), onClick: () => setShowImport(true), hidden: !canCreate },
          { icon: Copy,      label: t('contacts.duplicates'), onClick: () => setShowDupes(true), divider: true },
          { icon: Phone,     label: t('contacts.normalizeAll'), onClick: handleNormalizeAll },
        ]} />

        {canCreate && (
          <button onClick={() => navigate('/contacts/new')} className="btn-md btn-primary shrink-0 flex items-center gap-2">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">{t('contacts.newContact')}</span>
          </button>
        )}
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-ink-disabled" />
            </div>
            <p className="text-sm font-medium text-ink-secondary">{t('contacts.noContacts')}</p>
            <p className="text-xs text-ink-tertiary mt-1">{search ? t('contacts.noResults') : t('contacts.addFirst')}</p>
            {!search && canCreate && (
              <button onClick={() => navigate('/contacts/new')} className="btn-md btn-primary mt-4">
                <Plus className="w-4 h-4" /> {t('contacts.add')}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    {/* Checkbox column */}
                    <th className="px-4 py-3 w-10">
                      <button onClick={toggleAll}
                        className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                          allChecked ? 'bg-primary-500 border-primary-500 text-white' :
                          someChecked ? 'bg-primary-100 border-primary-400 text-primary-600' :
                          'border-surface-300 hover:border-primary-300'
                        }`}>
                        {(allChecked || someChecked) && <Check className="w-2.5 h-2.5" />}
                      </button>
                    </th>
                    <th aria-sort={sort === 'name' ? 'ascending' : sort === '-name' ? 'descending' : 'none'} className="w-[30%] text-left text-xs font-semibold text-ink-secondary px-4 py-3 whitespace-nowrap">
                      <button type="button" onClick={() => toggleSort('name')} title={t('contacts.sortBy')} className="inline-flex items-center gap-1 hover:text-ink">
                        {t('contacts.thName')}
                        <span aria-hidden="true" className={sort.replace('-', '') === 'name' ? 'text-primary-700' : 'text-ink-disabled'}>{sort === 'name' ? '↑' : sort === '-name' ? '↓' : '↕'}</span>
                      </button>
                    </th>
                    {colVis.phone && <th className="w-[18%] min-w-[12rem] text-left text-xs font-semibold text-ink-secondary px-4 py-3 whitespace-nowrap">{t('contacts.thPhone')}</th>}
                    {colVis.assignee && <th className="w-[16%] min-w-[9rem] text-left text-xs font-semibold text-ink-secondary px-4 py-3 whitespace-nowrap">{t('contacts.thAssignee')}</th>}
                    {colVis.created && (
                      <th aria-sort={sort === 'createdAt' ? 'ascending' : sort === '-createdAt' ? 'descending' : 'none'} className="min-w-[8rem] text-left text-xs font-semibold text-ink-secondary px-4 py-3 whitespace-nowrap">
                        <button type="button" onClick={() => toggleSort('createdAt')} title={t('contacts.sortBy')} className="inline-flex items-center gap-1 hover:text-ink">
                          {t('contacts.thCreated')}
                          <span aria-hidden="true" className={sort.replace('-', '') === 'createdAt' ? 'text-primary-700' : 'text-ink-disabled'}>{sort === 'createdAt' ? '↑' : sort === '-createdAt' ? '↓' : '↕'}</span>
                        </button>
                      </th>
                    )}
                    {colVis.email && <th className="w-[20%] min-w-[14rem] text-left text-xs font-semibold text-ink-secondary px-4 py-3 whitespace-nowrap">{t('contacts.thEmail')}</th>}
                    {allCustomFields.filter(f => colVis[f.id]).map(field => (
                      <th key={field.id} className="w-px text-left text-xs font-semibold text-ink-tertiary px-4 py-3 whitespace-nowrap">{field.key}</th>
                    ))}
                    <th className="px-4 py-3 w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {contacts.map((c) => {
                    const isSelected = selected.has(c._id);
                    return (
                      <tr key={c._id}
                        onClick={() => navigate(`/contacts/${c._id}`)}
                        className={`transition-colors group cursor-pointer ${isSelected ? 'bg-primary-50/60' : 'hover:bg-surface-50'} ${c.blocked ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-3" onClick={e => toggleOne(c._id, e)}>
                          <button className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                            isSelected ? 'bg-primary-500 border-primary-500 text-white' :
                            'border-surface-300 hover:border-primary-300 opacity-0 group-hover:opacity-100'
                          }`}>
                            {isSelected && <Check className="w-2.5 h-2.5" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 min-w-0">
                            {c.contactNumber && <span className="text-xs font-semibold text-ink-tertiary shrink-0">#{c.contactNumber}</span>}
                            <p className="font-medium text-ink truncate">{srv(c.name)}</p>
                            {c.blocked && <span className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full shrink-0">{t('contacts.blocked')}</span>}
                            {isReminderOverdue(c.reminderAt) && (
                              <AlertCircle className="w-3.5 h-3.5 text-red-600 shrink-0" title={t('contacts.reminderOverdue')} />
                            )}
                          </div>
                        </td>
                        {colVis.phone && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            {c.phone ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="text-sm tabular-nums text-ink-secondary">{fmtPhone(c.phone)}</span>
                                <CallButton phone={c.phone} iconClassName="w-3.5 h-3.5"
                                  className="p-1 rounded-lg text-ink-tertiary hover:text-green-700 hover:bg-green-50 transition-colors" />
                              </span>
                            ) : <span className="text-ink-tertiary">—</span>}
                          </td>
                        )}
                        {colVis.assignee && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-ink-secondary truncate max-w-[180px] block">
                              {(c.assignees?.length ? c.assignees : (c.assignedTo ? [c.assignedTo] : [])).map(u => u.name).filter(Boolean).join(', ') || <span className="text-ink-tertiary">—</span>}
                            </span>
                          </td>
                        )}
                        {colVis.created && <td className="px-4 py-3 whitespace-nowrap"><span className="text-sm tabular-nums text-ink-secondary">{formatDate(c.createdAt)}</span></td>}
                        {colVis.email && <td className="px-4 py-3"><span className="text-sm text-ink-secondary truncate max-w-[220px] block">{c.email || <span className="text-ink-tertiary">—</span>}</span></td>}
                        {allCustomFields.filter(f => colVis[f.id]).map(field => {
                          const rendered = renderFieldValue(field, c.customFieldValues?.[field.id]);
                          return (
                            <td key={field.id} className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-ink-secondary">{rendered ?? <span className="text-ink-tertiary">—</span>}</span>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={e => { e.stopPropagation(); navigate(`/contacts/${c._id}`); }}
                              className="p-1.5 rounded-lg text-ink-tertiary hover:text-primary-600 hover:bg-primary-50 transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {canDelete && (
                              <button onClick={e => { e.stopPropagation(); setDeleting(c); }}
                                className="p-1.5 rounded-lg text-ink-tertiary hover:text-red-600 hover:bg-red-50 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobil kartochka ro'yxati — jadval o'rniga */}
            <div className="md:hidden divide-y divide-surface-100">
              {contacts.map((c) => {
                const isSelected = selected.has(c._id);
                return (
                  <div key={c._id}
                    onClick={() => navigate(`/contacts/${c._id}`)}
                    className={`flex items-center gap-3 px-4 py-3 active:bg-surface-50 transition-colors ${isSelected ? 'bg-primary-50/60' : ''} ${c.blocked ? 'opacity-60' : ''}`}>
                    <button onClick={e => toggleOne(c._id, e)}
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                        isSelected ? 'bg-primary-500 border-primary-500 text-white' : 'border-surface-300'
                      }`}>
                      {isSelected && <Check className="w-2.5 h-2.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {c.contactNumber && <span className="text-xs font-semibold text-ink-tertiary shrink-0">#{c.contactNumber}</span>}
                        <p className="font-medium text-ink truncate">{srv(c.name)}</p>
                        {c.blocked && <span className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full shrink-0">{t('contacts.blocked')}</span>}
                        {isReminderOverdue(c.reminderAt) && (
                          <AlertCircle className="w-3.5 h-3.5 text-red-600 shrink-0" title={t('contacts.reminderOverdue')} />
                        )}
                      </div>
                      {(colVis.phone && c.phone) && <p className="text-xs text-ink-tertiary truncate mt-0.5">{fmtPhone(c.phone)}</p>}
                      {(colVis.email && c.email) && <p className="text-xs text-ink-tertiary truncate">{c.email}</p>}
                    </div>
                    <CallButton phone={c.phone} className="p-2 rounded-xl text-green-700 bg-green-50 shrink-0" />
                    <ChevronRight className="w-4 h-4 text-ink-disabled shrink-0" />
                  </div>
                );
              })}
            </div>

            {pages > 1 && (
              <div className="px-4 lg:px-6 py-4 border-t border-surface-100">
                <Pagination page={page} pages={pages} total={total} limit={30} onPage={(p) => load(p)} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {deleting && (
        <DeleteConfirm
          name={deleting === 'bulk' ? undefined : deleting.name}
          count={deleting === 'bulk' ? selected.size : undefined}
          onConfirm={handleDelete}
          onClose={() => setDeleting(null)}
          loading={deleteLoading}
        />
      )}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { dispatch(invalidateContacts()); load(1); }} />}
      {showDupes  && <DuplicatesModal onClose={() => setShowDupes(false)} onMerged={() => { dispatch(invalidateContacts()); load(1); }} />}
    </div>
  );
}
